import React, { useEffect, useMemo, useRef, useState } from 'react';
import mapboxgl from 'mapbox-gl';
import { useNavigate } from 'react-router-dom';
import AnimalCollectionMenu from '../components/AnimalCollectionMenu';
import AnimalExclusionFilters from '../components/AnimalExclusionFilters';
import BulkCollectionActions from '../components/BulkCollectionActions';
import {
  appendExclusionParams,
  useAnimalExclusions
} from '../utils/useAnimalExclusions';
import './ExplorePage.css';

// Your existing public Mapbox site token.
mapboxgl.accessToken =
  'pk.eyJ1IjoiaHVnbzFqYW52aWVyIiwiYSI6ImNtNjRhZHN4YjFldGwydnB5MXVqNmhicGsifQ.dJnEsRVlLaHj6T3b8wt8GQ';

const API_URL = (process.env.REACT_APP_API_URL || '').replace(/\/+$/, '');
const MAX_BULK_SELECTION = 500;

function getImageUrl(path) {
  if (!path) return null;
  if (/^https?:\/\//i.test(path)) return path;
  return `${API_URL}/${path.replace(/^\/+/, '')}`;
}

function ExplorePage() {
  const mapContainer = useRef(null);
  const markerRef = useRef(null);
  const [selectedPoint, setSelectedPoint] = useState(null);
  const [animals, setAnimals] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [selectMode, setSelectMode] = useState(false);
  const [selected, setSelected] = useState(() => new Set());
  const [selectionError, setSelectionError] = useState('');
  const { exclusions, setExclusion } = useAnimalExclusions();
  const navigate = useNavigate();

  // Initialize the map once. Data fetching is a separate effect so
  // changing exclusion filters re-runs the SAME geographic search.
  useEffect(() => {
    const map = new mapboxgl.Map({
      container: mapContainer.current,
      style: 'mapbox://styles/mapbox/streets-v11',
      center: [0, 20],
      zoom: 1.5
    });

    map.addControl(new mapboxgl.FullscreenControl());

    map.on('click', event => {
      const { lng, lat } = event.lngLat;
      markerRef.current?.remove();
      markerRef.current = new mapboxgl.Marker()
        .setLngLat([lng, lat])
        .addTo(map);
      setSelectedPoint({ lng, lat });
      setAnimals([]);
    });

    return () => {
      markerRef.current?.remove();
      markerRef.current = null;
      map.remove();
    };
  }, []);

  useEffect(() => {
    if (!selectedPoint) return undefined;
    const controller = new AbortController();

    async function loadPoint() {
      setLoading(true);
      setError('');
      setAnimals([]);
      try {
        const params = new URLSearchParams({
          lng: String(selectedPoint.lng),
          lat: String(selectedPoint.lat)
        });
        appendExclusionParams(params, exclusions);

        const response = await fetch(
          `${API_URL}/explore?${params.toString()}`,
          { signal: controller.signal }
        );
        const data = await response.json();
        if (!response.ok) {
          throw new Error(data.error || 'Unable to retrieve animals.');
        }
        if (!Array.isArray(data)) {
          throw new Error('Invalid animal data received.');
        }
        if (!controller.signal.aborted) setAnimals(data);
      } catch (err) {
        if (err.name !== 'AbortError') {
          console.error('Explore error:', err);
          setError(err.message);
        }
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    }

    loadPoint();
    return () => controller.abort();
    }, [selectedPoint, exclusions]);

  function toggleSelectMode() {
    setSelectMode(previous => !previous);
    setSelected(new Set());
    setSelectionError('');
  }

  function toggleAnimal(id) {
    if (!selected.has(id) && selected.size >= MAX_BULK_SELECTION) {
      setSelectionError('Select up to 500 animals at once.');
      return;
    }
    setSelectionError('');
    setSelected(previous => {
      const next = new Set(previous);
      if (next.has(id)) next.delete(id);
      else if (next.size < MAX_BULK_SELECTION) next.add(id);
      return next;
    });
  }

  function selectAllResults() {
    const allIds = new Set([...selected, ...animals.map(animal => animal.id)]);
    setSelectionError(allIds.size > MAX_BULK_SELECTION
      ? 'Maximum 500 animals selected.' : '');
    setSelected(previous => {
      const next = new Set(previous);
      for (const animal of animals) {
        if (next.size >= MAX_BULK_SELECTION) break;
        next.add(animal.id);
      }
      return next;
    });
  }

  const selectedIds = useMemo(() => [...selected], [selected]);

  return (
    <div className="explore-page">
      <div className="map-container" ref={mapContainer} />

      <div className="results">
        <h2>Animals in this area</h2>

        <AnimalExclusionFilters
          exclusions={exclusions}
          onChange={setExclusion}
        />

        {!selectedPoint && (
          <p>Click anywhere on the map to discover which animals live in that area.</p>
        )}
        {loading && <p>Searching for animals...</p>}
        {error && <p role="alert" className="explore-error">{error}</p>}
        {selectedPoint && !loading && !error && animals.length === 0 && (
          <p>No animals found for this location with the selected filters.</p>
        )}

        {selectedPoint && !loading && !error && (animals.length > 0 || selectMode) && (
          <>
            <div className="explore-results-header">
              <p className="explore-count">{animals.length} animals found</p>
              <button
                type="button"
                className={selectMode ? 'explore-select-many active' : 'explore-select-many'}
                onClick={toggleSelectMode}
              >
                {selectMode ? 'Done selecting' : 'Select Many'}
              </button>
            </div>

            {selectMode && (
              <button
                type="button"
                className="explore-select-all"
                onClick={selectAllResults}
              >
                Select all results
              </button>
            )}
          </>
        )}

        {selectMode && (
          <>
            <BulkCollectionActions
              selectedIds={selectedIds}
              onClear={() => { setSelected(new Set()); setSelectionError(''); }}
            />
            {selectionError && (
              <p className="explore-error" role="alert">{selectionError}</p>
            )}
          </>
        )}

        {!loading && !error && animals.length > 0 && (
          <ul>
            {animals.map(animal => {
              const imageUrl = getImageUrl(animal.image_path);
              const name = animal.name || animal.sci_name || 'Unnamed animal';
              const chosen = selected.has(animal.id);

              return (
                <li
                  key={animal.id}
                  className={`explore-animal-row${chosen ? ' explore-animal-row--selected' : ''}`}
                  onClick={() => selectMode
                    ? toggleAnimal(animal.id)
                    : navigate(`/animal/${animal.id}`)}
                  onKeyDown={event => {
                    if (event.target === event.currentTarget &&
                      (event.key === 'Enter' || event.key === ' ')) {
                      event.preventDefault();
                      if (selectMode) toggleAnimal(animal.id);
                      else navigate(`/animal/${animal.id}`);
                    }
                  }}
                  role="button"
                  tabIndex={0}
                  aria-pressed={selectMode ? chosen : undefined}
                  aria-label={selectMode ? `Select ${name}` : `View ${name}`}
                >
                  {imageUrl ? (
                    <img src={imageUrl} alt={name} loading="lazy" />
                  ) : (
                    <div className="animal-card-placeholder">No image</div>
                  )}
                  <span className="explore-animal-name">{name}</span>

                  {selectMode ? (
                    <input
                      type="checkbox"
                      className="explore-animal-checkbox"
                      checked={chosen}
                      aria-label={`Select ${name}`}
                      onClick={event => event.stopPropagation()}
                      onChange={() => toggleAnimal(animal.id)}
                    />
                  ) : (
                    <AnimalCollectionMenu animalId={animal.id} />
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}

export default ExplorePage;
